import { FC, memo, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollectionFrameModel } from '@mango-engineering/maknet-client-sdk/lib/shop/page-structure/model/collectionFrame.model';
import { CollectionType } from '@mango-engineering/maknet-client-sdk/lib/shop/product/enum/collectionType.enum';
import { Collection } from '@mango-engineering/maknet-client-sdk/lib/shop/product/model/product.model';
import { useMediaQuery } from '@mui/material';
import { DeliverySlotItem } from '@makro-shopping/price-calculation-service';
import {
  COUNTRY,
  ProductCard as MFEProductCard,
  ProductCardV2 as MFEProductCardV2,
  ProductCardV3 as MFEProductCardV3,
  ProductCompactCard as MFEProductCompactCard,
  ProductCompactMiniCard as MFEProductCompactMiniCard,
} from '@makro-shopping/product-card';
import { useProductCardLogic } from '@makro-shopping/use-product-card-logic';
import { ShoppingDataLayerProduct } from '@makro-shopping/use-product-card-logic/dist/types/product.type';
import { config, getAsJson } from '../../config/env';
import { ANALYTICS } from '../../constants/analytics';
import { useDynamicData, useStaticData } from '../../contexts/DataProvider.context';
import { useAskToLogin } from '../../hooks/useAskToLogin';
import { useBasket } from '../../hooks/useBasket';
import { useCounter } from '../../hooks/useCounter';
import { useCurrentBasketItem } from '../../hooks/useCurrentBasketItem';
import { useFloatingQuantityPicker } from '../../hooks/useFloatingQuantityPicker';
import { useHideAlcoholProductPrice } from '../../hooks/useHideAlcoholProductPrice';
import { useProductAvailability } from '../../hooks/useProductAvailability';
import { useProductOptionSelector } from '../../hooks/useProductOptionSelector';
import usePromotionRibbon from '../../hooks/usePromotionRibbon';
import { useQuantityPicker } from '../../hooks/useQuantityPicker';
import ProductService from '../../services/product/product.service';
import SendBrowserEvent from '../../services/sendBrowserEvent.service';
import { PreviewBasketItem } from '../../types/basket.types';
import { Product, ProductSearchResult } from '../../types/product.types';
import { getFeatureMinVersion, isFeatureEnabled } from '../../utils/common.util';
import { FALLBACK_APP_VERSION, ProductCardVariant } from '../../utils/constants';
import { getPromotionTagStyle, isFlagEnabledForCountry } from '../../utils/helper';
import {
  getIdFromString,
  getLocalizedPercentageSoldText,
  getWeightDisplayAndPartialQuantitySettings,
  prepareProductForProductCard,
} from '../../utils/product.util';
import { mergeAfcStoreCodes } from '../../utils/storeCodes';

interface ProductCardProps {
  product: Product | ProductSearchResult | ShoppingDataLayerProduct;
  type: ProductCardVariant;
  locale: string;
  onCardClick: (scrollToVariants?: boolean) => void;
  redirectToLogin: () => void;
  isLoggedIn: boolean;
  itemIndex?: number;
  autoPlayVideo?: boolean;
  carouselAnalyticsProps?: any;
  isRecommendationCarousel?: boolean;
  triggerCarouselImpressionEvent?: () => void;
  isFromBasket?: boolean;
  productCardV3?: boolean;
  onVariantModalClose?: () => void;
  onAskToLoginOpen?: () => void;
  onAskToLoginClose?: () => void;
}

const ProductCard: FC<ProductCardProps> = ({
  product,
  type,
  locale,
  onCardClick,
  redirectToLogin,
  isLoggedIn,
  itemIndex = 0,
  autoPlayVideo = false,
  carouselAnalyticsProps,
  isRecommendationCarousel = false,
  isFromBasket = false,
  triggerCarouselImpressionEvent,
  productCardV3 = false,
  onVariantModalClose,
  onAskToLoginOpen,
  onAskToLoginClose,
}) => {
  const { t, i18n } = useTranslation();
  const activeLocale = i18n.language || locale;
  const isMobile = useMediaQuery('(max-width: 767px)');

  const {
    featureFlags,
    userDetails,
    userSegments,
    isHorecaMember,
    countryCode,
    currentStoreCodes,
    mmid,
    isNative,
    appVersion,
    alcoholExpiryDate,
    expressStoreCodes,
    afcStoreCodes,
    isAfcStoreEnabled,
  } = useStaticData();
  const { isExpressDelivery, realtimeDeliverySlotData } = useDynamicData();
  const { previewBasketItems, isAlcoholProhibited, getBasketItemQuantity } = useBasket();

  const isThai = locale === 'th';
  const frameCollection = product?.collections
    ? (product.collections as Collection[])?.find((collection) => collection?.hasFrame)
    : null;
  const frameImageUrl = isThai ? frameCollection?.frameTh : frameCollection?.frameEn;
  const showFrame = !!frameImageUrl;

  const loyaltyPromotionsData = usePromotionRibbon(product as Product | ProductSearchResult);
  const { bgColor, txtColor } = getPromotionTagStyle(loyaltyPromotionsData.type);

  const isFloatingPickerEnabled =
    (featureFlags?.MFE_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER as boolean) ?? true;
  const isShoppingEnableProductCardV2 = (featureFlags?.SHOPPING_ENABLE_PRODUCT_CARD_V2_1 as boolean) ?? true;
  const enableHorizonProductCard = config.ENABLE_HORIZON_PRODUCT_CARD;
  const isProductNewTagEnabled = config.SHOPPING_ENABLE_PRODUCT_NEW_TAG;
  const isPromoTagV2Config = config.LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED;
  const isFetchRM3FromProductApi = config.LOYALTY_RM3_PROMOTION_FROM_PRODUCT_API;
  const enableRealtimeDeliverySlotConfig = config.ENABLE_REALTIME_DELIVERY_SLOT;
  const progressBarConfig = getAsJson(config.SHOPPING_PRODUCT_CARD_PROGRESS_BAR_CONFIG);
  const productCardVideoMinAppVersion = getFeatureMinVersion(
    countryCode,
    config.SHOPPING_ENABLE_PRODUCT_CARD_VIDEO_MIN_VERSION,
  );
  const isVideoEnabled = useMemo(
    () =>
      Boolean(
        productCardVideoMinAppVersion &&
          isFlagEnabledForCountry(countryCode, config.SHOPPING_ENABLE_PRODUCT_CARD_VIDEO) &&
          isFeatureEnabled(productCardVideoMinAppVersion, appVersion || FALLBACK_APP_VERSION),
      ),
    [appVersion, countryCode, productCardVideoMinAppVersion],
  );
  const horecaEnabledFromConfig = config.SHOPPING_ENABLE_HORECA;
  const horecaMinAppVersion = getFeatureMinVersion(countryCode, config.SHOPPING_ENABLE_HORECA_MIN_VERSION);
  const isHorecaEnabled = useMemo(
    () =>
      Boolean(
        horecaMinAppVersion &&
          isFlagEnabledForCountry(countryCode, horecaEnabledFromConfig) &&
          isFeatureEnabled(horecaMinAppVersion, appVersion || FALLBACK_APP_VERSION),
      ),
    [appVersion, countryCode, horecaEnabledFromConfig, horecaMinAppVersion],
  );
  const isPaidMemberPrice = Boolean(
    (
      product as typeof product & {
        paidMemberPromotion?: { isPaidMemberPrice?: boolean };
      }
    ).paidMemberPromotion?.isPaidMemberPrice,
  );
  const isHorecaMemberEnabled = Boolean(isHorecaEnabled && isHorecaMember);
  const horecaFlagConfig = {
    MFE_SHOPPING_ENABLE_HORECA: isHorecaMemberEnabled && isPaidMemberPrice,
  };
  const hideAlcoholProductPrice = isFlagEnabledForCountry(countryCode, config.ALCOHOL_HIDE_PRODUCT_PRICE);
  const hasAlcoholProductAvailability = isFlagEnabledForCountry(countryCode, config.ALCOHOL_PRODUCT_AVAILABILITY);
  const { status: productStatus } = useProductAvailability(
    product as Product,
    isExpressDelivery,
    hasAlcoholProductAvailability,
  );
  const isAlcoholNotEligible = useHideAlcoholProductPrice({
    product,
    productStatus,
    hideAlcoholProductPrice,
  });
  const isAlcoholSaleProhibited = isAlcoholProhibited && Boolean(product?.isAlcohol);

  const showKg = product?.isWeightedFresh;
  const [activeShowKg, setActiveShowKg] = useState(showKg);
  const enablePartialQuantity = showKg && product?.moq === 1 && product?.sellInPiece === false;
  const [activeEnablePartialQuantity, setActiveEnablePartialQuantity] = useState(enablePartialQuantity);
  const [activeMoq, setActiveMoq] = useState(product?.moq || 1);
  const [maxInventory, setMaxInventory] = useState(product.totalInventory || 999);
  const { basketItem } = useCurrentBasketItem(product);

  const isRealtimeDeliverySlotEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, enableRealtimeDeliverySlotConfig));
  }, [countryCode, enableRealtimeDeliverySlotConfig]);

  const isHorizonProductCardEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, enableHorizonProductCard) && isNative);
  }, [countryCode, enableHorizonProductCard, isNative]);

  const isHorizonUiUxEnabled = useMemo(() => {
    const horizonUiUxMinVersion = getFeatureMinVersion(countryCode, config.HORIZON_UI_UX_MIN_VERSION);
    return isHorizonProductCardEnabled && isFeatureEnabled(horizonUiUxMinVersion, appVersion || FALLBACK_APP_VERSION);
  }, [appVersion, countryCode, isHorizonProductCardEnabled]);

  const navigateVariantToPdp = useMemo(() => {
    return isFeatureEnabled('1.81.0', appVersion || FALLBACK_APP_VERSION);
  }, [appVersion]);

  const isPromoTagV2Enabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, isPromoTagV2Config) && isNative && isHorizonUiUxEnabled);
  }, [countryCode, isPromoTagV2Config, isNative, isHorizonUiUxEnabled]);

  const shouldUseProductCardV3 = productCardV3 && isHorizonUiUxEnabled;

  const updatedProducts =
    product.id === basketItem?.productDetails?.id
      ? {
          ...product,
          productInBasketCount: basketItem?.quantity || 0,
        }
      : {
          ...product,
        };

  const isProductOptionSelectorEnabled =
    config.SHOPPING_ENABLE_PRODUCT_OPTION_SELECTOR &&
    updatedProducts &&
    updatedProducts.options &&
    updatedProducts.options.length > 0 &&
    updatedProducts.otherProducts &&
    updatedProducts.otherProducts.length > 0;

  const fetchVariantPriceRef = useRef<((variantProductId: string) => Promise<Product | null>) | null>(null);

  const { togglePicker, showPicker, closePicker } = useFloatingQuantityPicker({
    id: `${product.id}_${product.makroId || product.id}`,
  });

  const showQuantityPicker = showPicker && isFloatingPickerEnabled;

  const analyticsProps = useMemo(
    () => ({
      ...carouselAnalyticsProps,
    }),
    [carouselAnalyticsProps],
  );

  const currentBasketQuantity = getBasketItemQuantity(getIdFromString(product?.id));

  const countdownTimerCollections = product?.collections
    ? product?.collections.find((collection) => collection.type === CollectionType.COUNTDOWN_TIMER)
    : null;

  const { countdownStartTimeMs, countdownEndTimeMs, isCountdownWindowValid } = useMemo(() => {
    const start = countdownTimerCollections?.countdownTimer?.startDateTime;
    const end = countdownTimerCollections?.countdownTimer?.endDateTime;
    const startMs = start ? Date.parse(String(start)) : NaN;
    const endMs = end ? Date.parse(String(end)) : NaN;

    return {
      countdownStartTimeMs: startMs,
      countdownEndTimeMs: endMs,
      isCountdownWindowValid: Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs,
    };
  }, [countdownTimerCollections]);
  const [countdownEvaluationTime, setCountdownEvaluationTime] = useState(() => Date.now());

  useEffect(() => {
    if (!isCountdownWindowValid) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const updateCountdownWindow = () => {
      const currentTime = Date.now();
      setCountdownEvaluationTime(currentTime);

      const nextBoundary =
        currentTime < countdownStartTimeMs
          ? countdownStartTimeMs
          : currentTime < countdownEndTimeMs
            ? countdownEndTimeMs
            : undefined;

      if (nextBoundary !== undefined) {
        timeoutId = setTimeout(updateCountdownWindow, Math.max(1, nextBoundary - currentTime + 1));
      }
    };

    updateCountdownWindow();

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isCountdownWindowValid, countdownStartTimeMs, countdownEndTimeMs]);

  // Keep future countdowns mounted so the timer can start on schedule; ignore expired campaigns.
  const showCountdownTimer = isCountdownWindowValid && countdownEvaluationTime < countdownEndTimeMs;
  const isCountdownTimerActive =
    showCountdownTimer &&
    countdownEvaluationTime >= countdownStartTimeMs &&
    countdownEvaluationTime < countdownEndTimeMs;

  const baseStoreCodes =
    isExpressDelivery && expressStoreCodes.length > 0
      ? expressStoreCodes
      : currentStoreCodes.length > 0
        ? currentStoreCodes
        : [];
  const storeCodes = mergeAfcStoreCodes(baseStoreCodes, afcStoreCodes, isAfcStoreEnabled);

  const productStatusData = {
    storeCodes: storeCodes,
    alcoholExpiryDate,
    enable3pOwnfleet: config.ENABLE_3P_OWNFLEET,
  };

  const {
    count,
    inProgress,
    onAdd,
    onIncrement,
    onChange,
    onDecrement,
    onDelete,
    onDeleteWithProductId,
    onAddToBasketWithQuantity,
  } = useCounter({
    initialCount: currentBasketQuantity,
    enablePartialQuantity,
    product: product as Product,
    closePicker,
    analyticsProps,
    indexClicked: itemIndex,
    categoryAnalytics: undefined,
    isRecommendationCarousel,
    triggerCarouselImpressionEvent,
    isFromBasket,
  });

  const {
    formattedValue,
    minimumQuantity,
    maximumQuantity,
    handleDelete,
    handleDecrement,
    handleDecrementMouseDown,
    handleMouseUp,
    handleDecrementMouseLeave,
    handleContextMenu,
    handleIncrement,
    handleIncrementMouseDown,
    handleIncrementMouseLeave,
    isInputFocused,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    resetValue,
    setValueDirectly,
  } = useQuantityPicker({
    max: maxInventory,
    disabled: inProgress,
    onChange: onChange,
    onIncrement: onIncrement,
    onDecrement: onDecrement,
    showKg: showKg,
    moq: product?.moq,
    isPDP: false,
    initialValue: count,
    onDelete: onDelete,
    enablePartialQuantity: activeEnablePartialQuantity,
    callApiOnChange: !isProductOptionSelectorEnabled,
  });

  const {
    handleOptionClick,
    productOptionSelectorProps,
    selectedOptions,
    handleOptionSelect,
    modalHandleDecrement,
    handleModalDelete,
    getSelectedProductId,
    selectedProductBasketQuantity,
    totalBasketQuantity,
    isSelectedVariantInStock,
    allOptionsSelected,
    selectedVariantInventory,
    variantPrice,
    variantMoq,
    selectedVariantProduct,
  } = useProductOptionSelector({
    productData: updatedProducts as unknown as Product,
    handleDecrement,
    formattedValue: formattedValue as number,
    minimumQuantity,
    isLoggedIn: isLoggedIn,
    resetValue,
    onDeleteWithProductId,
    onFetchVariantProduct: useCallback((variantProductId: string) => {
      if (fetchVariantPriceRef.current) {
        return fetchVariantPriceRef.current(variantProductId);
      }
      return Promise.resolve(null);
    }, []),
    onModalClose: () => {
      resetValue();
      onVariantModalClose?.();
    },
    onModalDelete: (isLoggedIn: boolean) => {
      if (isLoggedIn) {
        onDelete?.();
      }
    },
    showKg: !!showKg,
    moq: product?.moq || 1,
    analyticsProps,
    isHorizonUiUxEnabled,
  });

  useEffect(() => {
    if (selectedVariantProduct) {
      const settings = getWeightDisplayAndPartialQuantitySettings({
        moq: selectedVariantProduct.moq ?? product?.moq,
        isWeightedFresh: selectedVariantProduct.isWeightedFresh ?? product?.isWeightedFresh,
        sellInPiece: selectedVariantProduct.sellInPiece ?? product?.sellInPiece,
      });
      setActiveShowKg(settings?.showKg ?? !!product?.isWeightedFresh);
      setActiveEnablePartialQuantity(settings?.enablePartialQuantity ?? false);
    } else {
      setActiveShowKg(showKg);
      setActiveEnablePartialQuantity(enablePartialQuantity);
    }
  }, [selectedVariantProduct, product, showKg, enablePartialQuantity]);

  const handleOptionClickWithReset = useCallback(() => {
    const qty = selectedProductBasketQuantity > 0 ? selectedProductBasketQuantity : 1;
    setValueDirectly(qty);
    handleOptionClick();
  }, [selectedProductBasketQuantity, setValueDirectly, handleOptionClick]);

  useEffect(() => {
    if (variantMoq !== null) {
      setActiveMoq(variantMoq);
      const basketQty = selectedProductBasketQuantity > 0 ? selectedProductBasketQuantity : 1;
      const updatedQty = activeShowKg ? basketQty * variantMoq : basketQty;
      setValueDirectly(updatedQty);
    } else {
      setActiveMoq(product?.moq || 1);
    }
  }, [variantMoq, product?.moq, selectedProductBasketQuantity, setValueDirectly, activeShowKg]);

  fetchVariantPriceRef.current = useCallback(
    async (variantProductId: string): Promise<Product | null> => {
      try {
        const storeCodes = currentStoreCodes.length > 0 ? currentStoreCodes : [];
        const variantProduct = await ProductService.getProduct(
          variantProductId,
          storeCodes,
          activeLocale || '',
          undefined, // isSalesCustomer
          undefined, // is3P
          true, // enableRmsStatus
          mmid, // membershipId
          undefined, // shouldFetchDeliveryDescription
          undefined, // isSameDayAllowedForStore
          undefined, // isProPointsEnabled
          undefined, // selectedDeliverySlotDate
          undefined, // isUnavailableReasonEnabled
          isExpressDelivery, // isExpress
          undefined, // is3PComplexPromotionEnabled
          config.SHOPPING_ENABLE_SECONDARY_PRICING, // isSecondaryPricingEnabled
          isProductOptionSelectorEnabled || false, // isProductOptionSelectorEnabled
          countryCode as COUNTRY, // countryCode
          isProductNewTagEnabled, // isProductNewTagEnabled
          undefined, // shouldFetchPdpVideo
          isFetchRM3FromProductApi, // isLoyaltyRm3PromotionFromProductApiEnabled
          afcStoreCodes, // afcStoreCodes
          isAfcStoreEnabled, // isAfcStoreCodesEnabled
          isPromoTagV2Enabled,
          userSegments,
          isHorecaMemberEnabled,
        );
        return (variantProduct as unknown as Product) ?? null;
      } catch (error) {
        console.error('Failed to fetch variant product:', error);
        return null;
      }
    },
    [
      currentStoreCodes,
      mmid,
      isExpressDelivery,
      isProductOptionSelectorEnabled,
      isProductNewTagEnabled,
      countryCode,
      activeLocale,
      afcStoreCodes,
      isAfcStoreEnabled,
      isFetchRM3FromProductApi,
      isPromoTagV2Enabled,
      userSegments,
      isHorecaMemberEnabled,
    ],
  );

  useEffect(() => {
    if (selectedVariantInventory > 0) {
      setMaxInventory(selectedVariantInventory);
    } else {
      setMaxInventory(product.totalInventory || 999);
    }
  }, [selectedVariantInventory, product.totalInventory]);

  const prevSelectedProductBasketQuantityRef = useRef(selectedProductBasketQuantity);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = productOptionSelectorProps.open && !prevOpenRef.current;
    const qtyChanged = prevSelectedProductBasketQuantityRef.current !== selectedProductBasketQuantity;

    if (productOptionSelectorProps.open && (justOpened || qtyChanged)) {
      const newQuantity = selectedProductBasketQuantity > 0 ? selectedProductBasketQuantity : 1;
      const updatedQty = showKg ? newQuantity * (productOptionSelectorProps?.moq || 1) : newQuantity;
      setValueDirectly(updatedQty);
    }

    prevOpenRef.current = productOptionSelectorProps.open;
    prevSelectedProductBasketQuantityRef.current = selectedProductBasketQuantity;
  }, [
    selectedProductBasketQuantity,
    setValueDirectly,
    productOptionSelectorProps.open,
    activeShowKg,
    productOptionSelectorProps?.moq,
    showKg,
  ]);

  useEffect(() => {
    if (prevOpenRef.current) {
      SendBrowserEvent.triggerProductOptionSelectorAnalytics(ANALYTICS.PRODUCT_OPTION_SELECTOR_OPEN, {
        productId: product?.id,
        productPrice: product?.displayPrice,
        sku: product?.sku || product?.variants?.[0]?.sku || '',
        makroId: product?.makroId || '',
        ...analyticsProps,
      });
    }
  }, [
    productOptionSelectorProps.open,
    product?.displayPrice,
    product?.id,
    product?.makroId,
    product?.sku,
    product?.variants,
    analyticsProps,
  ]);

  const { askToLoginProps, loginOrApplyTitle, loginOrApplyDesc, loginOrApplyButtonText } = useAskToLogin({
    product,
    promotionData: {
      collectionId: loyaltyPromotionsData.collectionId,
      collectionHandle: loyaltyPromotionsData.collectionHandle,
      isComplexPromotion: loyaltyPromotionsData.isComplexPromotion,
      productId: loyaltyPromotionsData.productId,
    },
    isFromBasket,
    isHorizonUiUxEnabled,
    onModalOpen: onAskToLoginOpen,
    onModalClose: onAskToLoginClose,
  });

  const preventDefault = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  const cols = product?.collections;

  function isCollectionArray(a: unknown): a is Collection[] {
    return Array.isArray(a) && a.every((x) => x && typeof x === 'object' && 'frame' in x);
  }

  const frameCollections = isCollectionArray(cols)
    ? cols.find((collection) => {
        return collection.frame != null;
      })
    : undefined;

  const appFrame = frameCollections?.frame?.find((frameItem: CollectionFrameModel) => frameItem.platform === 'APP');

  const webFrame = frameCollections?.frame?.find((frameItem: CollectionFrameModel) => frameItem.platform === 'WEB');

  const finalFrame = isMobile ? appFrame : webFrame;

  const frameStyles = Object.fromEntries(
    Object.entries({
      top: finalFrame?.top,
      bottom: finalFrame?.bottom,
      left: finalFrame?.left,
      right: finalFrame?.right,
      scale: finalFrame?.scale,
      frameImage: finalFrame?.frameImage ?? '',
      frameImageEn: finalFrame?.frameImageEn ?? '',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }).filter(([_, value]) => value != null && value !== 0),
  );

  const preparedProduct = prepareProductForProductCard(product as ShoppingDataLayerProduct, isHorizonUiUxEnabled);

  const { getProductItems } = useProductCardLogic({
    product: {
      ...preparedProduct,
      totalPercentageSoldText: getLocalizedPercentageSoldText(preparedProduct, t),
      productInBasketCount:
        (previewBasketItems &&
          previewBasketItems?.length > 0 &&
          (previewBasketItems.find((basketItem: PreviewBasketItem) => basketItem?.id === getIdFromString(product?.id))
            ?.quantitySetByUser as number)) ||
        0,
    },
    cardVariant: type,
    serviceConfig: {
      translations: {
        notDeliverable: t('product.notDeliverable'),
        notEligible: t('product.notEligible'),
        outOfStock: isHorizonProductCardEnabled ? t('product.soldOut') : t('product.outOfStock'),
        kg: t('product.kg'),
        minimumOrderKg: t('product.minimumOrderKg'),
        units: t('product.units'),
        per: t('product.per'),
        buy: isShoppingEnableProductCardV2 && !isHorizonUiUxEnabled ? '' : t('product.buy'),
        viewAll: t('product.viewAll'),
        restricted: t('product.restricted'),
        notAvailable: t('product.unavailable'),
        loginOrApplyTitle: loginOrApplyTitle,
        newTag: t('product.newTag'),
        loginOrApplyDesc: loginOrApplyDesc,
        loginOrApplyButtonText: loginOrApplyButtonText,
        deliveryByLabel: t('product.deliveryByLabel'),
        preOrderLabel: t('product.preOrderLabel'),
        expressDeliveryLabel: t('product.expressDeliveryLabel'),
        sameDayDeliveryLabel: t('product.sameDayDeliveryLabel'),
        nextDayDeliveryLabel: t('product.nextDayDeliveryLabel'),
        deliveryDaysLabel: t('product.deliveryDaysLabel'),
      },
      configs: {
        MFE_SHOPPING_PRODUCT_WF_COMMUNICATION_ENABLE:
          (featureFlags?.MFE_SHOPPING_PRODUCT_WF_COMMUNICATION_ENABLE as boolean) ?? true,
        MFE_SHOPPING_PRODUCTLIST_PROPOINT_PROMOTION_ENABLE:
          (featureFlags?.MFE_SHOPPING_PRODUCTLIST_PROPOINT_PROMOTION_ENABLE as boolean) ?? true,
        MFE_SHOPPING_ENABLE_DISCOUNT_PERCENTAGE_REVAMP:
          (featureFlags?.MFE_SHOPPING_ENABLE_DISCOUNT_PERCENTAGE_REVAMP as boolean) ?? true,
        MFE_LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED: isPromoTagV2Enabled && isHorizonUiUxEnabled,
        MFE_SHOPPING_ENABLE_ENHANCED_MARKETING_BADGE: isCountdownTimerActive
          ? false
          : config.SHOPPING_ENABLE_ENHANCED_MARKETING_BADGE,
        MFE_SHOPPING_ENABLE_UNIFIED_BASKET: config.SHOPPING_ENABLE_UNIFIED_BASKET && isNative,
        MFE_SHOPPING_ENABLE_PRODUCT_CARD_V2: config.SHOPPING_ENABLE_PRODUCT_CARD_V2_1 ?? true,
        MFE_SHOPPING_ENABLE_SECONDARY_PRICING: config.SHOPPING_ENABLE_SECONDARY_PRICING ?? true,
        MFE_SHOPPING_ENABLE_PRODUCT_NEW_TAG: isProductNewTagEnabled,
        MFE_SHOPPING_ENABLE_PROGRESS_BAR_ON_PRODUCT_CARD: config.SHOPPING_ENABLE_PROGRESS_BAR_ON_PRODUCT_CARD,
        MFE_SHOPPING_PRODUCT_CARD_PROGRESS_BAR_CONFIG: progressBarConfig,
        MFE_SHOPPING_ENABLE_HORIZON_PRODUCT_CARD: isHorizonUiUxEnabled,
        MFE_SHOPPING_ENABLE_REALTIME_DELIVERY_INFO: isRealtimeDeliverySlotEnabled && isHorizonUiUxEnabled,
        MFE_SHOPPING_ENABLE_PRODUCT_CARD_VIDEO: isVideoEnabled,
        ...horecaFlagConfig,
      },
      ...(isRealtimeDeliverySlotEnabled &&
        isHorizonUiUxEnabled && {
          realtimeDeliverySlots: (realtimeDeliverySlotData || []) as DeliverySlotItem[],
        }),
      activeLocale: activeLocale,
      isMobile: true,
    },
    onClick: () => {
      if (onCardClick) {
        onCardClick(Boolean(isProductOptionSelectorEnabled));
      }
    },
    isAlcoholNotEligible: isAlcoholNotEligible,
    isAlcoholSaleProhibited,
    loyaltyPromotionsData: {
      ...loyaltyPromotionsData,
      titleTextColor: txtColor,
      bgColor: bgColor,
    },
    countryCode: countryCode as COUNTRY,
    productStatusData: productStatusData,
  });

  if (product?.status === 'active') {
    getProductItems.notAvailableText = undefined;
  }

  const handleRedirectToLogin = () => {
    redirectToLogin();
    productOptionSelectorProps.onClose();
    askToLoginProps.onClose();
  };

  const productItems: any = {
    ...getProductItems,
    anchorElement: userDetails?.userId && isFloatingPickerEnabled ? 'div' : 'a',
    showFrame: isHorizonUiUxEnabled ? undefined : showFrame,
    frameImage: frameImageUrl,
    togglePicker,
    showQuantityPicker: count ? showQuantityPicker : false,
    min: minimumQuantity,
    max: maximumQuantity,
    value: formattedValue,
    handleIncrement,
    handleDelete,
    handleDecrement,
    handleDecrementMouseDown,
    handleMouseUp,
    handleDecrementMouseLeave,
    handleContextMenu,
    handleIncrementMouseDown,
    handleIncrementMouseLeave,
    preventDefault,
    onAdd,
    isLoggedIn,
    redirectToLogin: handleRedirectToLogin,
    redirectToPdp: () =>
      onCardClick && onCardClick(Boolean(isProductOptionSelectorEnabled)),
    floatingPickerFeatureEnabled: isFloatingPickerEnabled,
    ...(isNative && {
      onClick: () => onCardClick && onCardClick(Boolean(isProductOptionSelectorEnabled)),
      anchorElement: 'div',
      url: undefined,
    }),
    openProductOptionSelector: handleOptionClickWithReset,
    showCountdownTimer: showCountdownTimer,
    startTime: countdownTimerCollections?.countdownTimer?.startDateTime,
    endTime: countdownTimerCollections?.countdownTimer?.endDateTime,
    showProductOptionSelector: productOptionSelectorProps.open,
    onProductOptionSelectorClose: productOptionSelectorProps.onClose,
    onProductOptionSelectorAddToCart: async () => {
      if ((formattedValue || minimumQuantity) > 0) {
        const quantity = ((formattedValue as number) || minimumQuantity || 1) / activeMoq;
        const productId = getSelectedProductId();
        await onAddToBasketWithQuantity(quantity, productId);
      }
      productOptionSelectorProps.onClose();
    },
    isInputFocused,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    productOptionSelectorFeatureEnabled: isProductOptionSelectorEnabled,
    productOptionSelectorProps: {
      ...productOptionSelectorProps,
      variantPrice,
      optionLabel: t('variants.optionsLabel'),
      inBasketLabel: t('variants.countLabel'),
    },
    options: productOptionSelectorProps?.options,
    otherProducts: product?.otherProducts,
    optionValues: product?.optionValues,
    selectedOptions: selectedOptions,
    onOptionSelect: handleOptionSelect,
    modalHandleDecrement,
    collectionFrame: isHorizonUiUxEnabled ? undefined : finalFrame,
    handleModalDelete,
    totalBasketQuantity,
    addToCartDisabled: !allOptionsSelected || !isSelectedVariantInStock,
    askToLoginProps: {
      openModal: askToLoginProps.openModal,
      onOpen: askToLoginProps.onOpen,
      onClose: askToLoginProps.onClose,
      redirectToApplyCardPage: askToLoginProps.redirectToApplyCardPage,
      redirectToPromotionPlp: askToLoginProps.redirectToPromotionPlp,
    },
    hasMMID: !!mmid,
    redirectVariantProductToPdp: navigateVariantToPdp,
  };

  return type === ProductCardVariant.COMPACT ? (
    <MFEProductCompactCard
      {...productItems}
      addToCartLoading={inProgress}
      data-testid="product-card-v2"
      onClick={onCardClick}
      locale={activeLocale}
      collectionFrame={frameStyles}
    />
  ) : type === ProductCardVariant.COMPACT_MINI ? (
    <MFEProductCompactMiniCard
      {...productItems}
      addToCartLoading={inProgress}
      data-testid="product-card-v2"
      onClick={onCardClick}
      locale={activeLocale}
      collectionFrame={frameStyles}
    />
  ) : isShoppingEnableProductCardV2 ? (
    <>
      {shouldUseProductCardV3 ? (
        <MFEProductCardV3
          {...productItems}
          addToCartLoading={inProgress}
          data-testid="product-card-v3"
          onClick={onCardClick}
          locale={activeLocale}
          autoPlayVideo={autoPlayVideo}
        />
      ) : (
        <MFEProductCardV2
          {...productItems}
          addToCartLoading={inProgress}
          data-testid="product-card-v2"
          onClick={onCardClick}
          locale={activeLocale}
          autoPlayVideo={autoPlayVideo}
          collectionFrame={frameStyles}
          isSearch={true}
        />
      )}
    </>
  ) : (
    <MFEProductCard
      {...productItems}
      addToCartLoading={inProgress}
      data-testid="product-card-v2"
      onClick={onCardClick}
      locale={activeLocale}
      collectionFrame={frameStyles}
    />
  );
};

export default memo(ProductCard, (prev, next) => {
  return (
    prev.product?.id === next.product?.id &&
    prev.isLoggedIn === next.isLoggedIn &&
    prev.type === next.type &&
    prev.autoPlayVideo === next.autoPlayVideo &&
    prev.locale === next.locale &&
    prev.isFromBasket === next.isFromBasket
  );
});
